import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CAT_LABELS = {
    pt: {
        estrutura: "Estrutura", cobertura: "Cobertura", equipamento: "Equipamento",
        ferragens: "Ferragens", energia: "Energia", iluminacao: "Iluminação",
        painel_solar: "Painel Solar", acessorio: "Acessório",
    },
    en: {
        estrutura: "Structure", cobertura: "Canopy", equipamento: "Equipment",
        ferragens: "Hardware", energia: "Power", iluminacao: "Lighting",
        painel_solar: "Solar Panel", acessorio: "Accessory",
    },
};

export default function AdminPartLabel() {
    const { partId } = useParams();
    const [params] = useSearchParams();
    const cartId = params.get("cart");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lang, setLang] = useState("pt");
    const [barcodeUrl, setBarcodeUrl] = useState(null);
    const [qrUrl, setQrUrl] = useState(null);

    // Build authenticated barcode + QR URLs as blob URIs (Authorization header can't be set on <img> directly)
    useEffect(() => {
        let bcObj = null, qrObj = null;
        (async () => {
            try {
                const res = await api.get(`/admin/parts/${partId}/barcode.png?size=small`, { responseType: "blob" });
                bcObj = URL.createObjectURL(res.data);
                setBarcodeUrl(bcObj);
            } catch { /* noop */ }
            try {
                const publicBase = window.location.origin;
                const res = await api.get(`/admin/parts/${partId}/qr.png?size=3&public_base=${encodeURIComponent(publicBase)}`, { responseType: "blob" });
                qrObj = URL.createObjectURL(res.data);
                setQrUrl(qrObj);
            } catch { /* noop */ }
        })();
        return () => {
            if (bcObj) URL.revokeObjectURL(bcObj);
            if (qrObj) URL.revokeObjectURL(qrObj);
        };
    }, [partId]);

    useEffect(() => {
        (async () => {
            try {
                if (!cartId) {
                    // Fallback to public lookup for essential info
                    const { data } = await api.get(`/carts/parts/${partId}`);
                    setData({
                        cart: data.cart,
                        part: data.part,
                    });
                } else {
                    const { data } = await api.get(`/admin/carts/${cartId}`);
                    const eq = data.equipment || {};
                    const p = (eq.assigned_parts || []).find((x) => x.id === partId);
                    setData({
                        cart: {
                            serial_number: eq.serial_number,
                            model: eq.model,
                            nickname: eq.nickname,
                        },
                        part: p,
                    });
                }
            } catch (err) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Erro a carregar etiqueta");
            } finally {
                setLoading(false);
            }
        })();
    }, [cartId, partId]);

    const categoryLabel = useMemo(() => {
        return CAT_LABELS[lang]?.[data?.part?.category] || "";
    }, [data, lang]);

    if (loading) return <div className="p-10 text-center text-muted-foreground">A carregar…</div>;
    if (!data?.part) return <div className="p-10 text-center text-muted-foreground">Peça não encontrada.</div>;

    const { part, cart } = data;

    return (
        <div className="min-h-screen bg-gray-100 py-6 print:bg-white print:py-0" data-testid="part-label-page">
            {/* Toolbar — hidden on print */}
            <div className="max-w-2xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
                <Link to={cartId ? `/admin/equipments` : "/"} className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline">
                    <ArrowLeft className="h-4 w-4" /> Voltar
                </Link>
                <div className="flex items-center gap-2">
                    <div className="inline-flex items-center gap-1 rounded-full bg-muted p-1" data-testid="label-lang-toggle">
                        <Globe className="h-3.5 w-3.5 text-primary/60 ml-1.5" />
                        {["pt", "en"].map((v) => (
                            <button key={v} onClick={() => setLang(v)}
                                data-testid={`label-lang-${v}`}
                                className={`rounded-full px-2.5 py-0.5 text-xs font-bold transition ${lang === v ? "bg-primary text-primary-foreground" : "text-primary hover:bg-white"}`}>
                                {v.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => window.print()}
                        data-testid="label-print-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:-translate-y-0.5 transition"
                    >
                        <Printer className="h-4 w-4" /> Imprimir etiqueta
                    </button>
                </div>
            </div>

            {/* Label sheet — sized 40×25mm minimum recomendado, printable */}
            <div className="mx-auto bg-white shadow-lg print:shadow-none label-card">
                <div className="p-1.5 h-full flex flex-col text-[6pt] leading-tight">
                    <div className="flex items-baseline justify-between gap-1">
                        <div className="font-heading font-black text-primary text-[8pt] leading-none tracking-tight">NOVIFOOD</div>
                        {categoryLabel && (
                            <span className="rounded-sm bg-primary/10 text-primary text-[5pt] uppercase tracking-wider font-bold px-1 py-px">
                                {categoryLabel}
                            </span>
                        )}
                    </div>

                    <div className="mt-0.5 flex-1 min-h-0">
                        <div className="font-bold text-primary text-[7pt] leading-tight truncate">{part.name}</div>
                        <div className="text-muted-foreground truncate">
                            {cart?.serial_number && <span className="font-mono">{cart.serial_number}</span>}
                            {part.sku && <span> · {part.sku}</span>}
                        </div>
                    </div>

                    <div className="mt-0.5 flex gap-1 items-end">
                        {/* QR square on the left */}
                        <div className="shrink-0 flex flex-col items-center">
                            {qrUrl ? (
                                <img src={qrUrl} alt={`qr ${part.id}`} data-testid="label-qr-img"
                                    className="object-contain"
                                    style={{ width: "9mm", height: "9mm", imageRendering: "crisp-edges" }} />
                            ) : (
                                <div style={{ width: "9mm", height: "9mm" }} className="bg-muted rounded-sm" />
                            )}
                            <div className="font-mono text-[4.5pt] tracking-wide text-muted-foreground mt-px">SCAN</div>
                        </div>
                        {/* Barcode + ID on the right */}
                        <div className="flex-1 min-w-0 flex flex-col items-center">
                            {barcodeUrl ? (
                                <img src={barcodeUrl} alt={`barcode ${part.id}`} className="h-6 max-w-full object-contain" data-testid="label-barcode-img" style={{ imageRendering: "crisp-edges" }} />
                            ) : (
                                <div className="h-6 flex items-center text-muted-foreground">Sem barcode</div>
                            )}
                            <div className="font-mono text-[5.5pt] tracking-widest leading-none">{part.id}</div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .label-card {
                    width: 40mm;
                    height: 25mm;
                    max-width: 100%;
                    box-sizing: border-box;
                    overflow: hidden;
                }
                @media print {
                    body { background: white !important; }
                    @page { size: 40mm 25mm; margin: 0; }
                    .label-card { box-shadow: none !important; margin: 0 !important; }
                }
            `}</style>
        </div>
    );
}
